import { getPageUrl } from "../../lib/stepUrl";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { keyframes, styled } from "goober";
import { createPortal } from "react-dom";
import { nanoid } from "nanoid";
import {
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
} from "react";
import type { ChatEntryWithSubTables } from "../../../shared/aiSdkMessages";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import {
  workflowPageViewSchema,
  type WorkflowPageView,
} from "../../../shared/workflowPageView";
import { useChatpadShortcut } from "../../hooks/useChatpadShortcut";
import { useSessionTenantEdgeUrlOptions } from "../../hooks/useSessionTenantOrigin";
import { useToolOutput } from "../../hooks/useToolOutput";
import { clientSendContext } from "../../lib/clientSendContext";
import { emitErrorToast } from "../../lib/errorToast";
import { personalVolumePath } from "../../lib/apiPaths";
import { resolveStorageFileHref } from "../../lib/resolveStorageFileHref";
import { uploadToolCallOutput } from "../../lib/toolCallOutputs";
import { showWorkflowPage } from "../../tools/ShowWorkflowPage";
import {
  approvePlan,
  askQuestion,
  bash,
  type ChatOutputView,
  type ChatTool,
  connectChatToApp,
  connectToApp,
  editFile,
  execute,
  memory,
  readFile,
  readSkillFile,
  readStorageFile,
  runSkillScript,
  setWorkflowSuggestion,
  showExecutionOutput,
  showStorageFile,
  skillTool,
  webFetch,
  webSearch,
  writeFile,
} from "../../tools";
import { Chat } from "../Chat";
import { ChatMessageNav, ChatMessageNavOverlay } from "../Chat/ChatMessageNav";
import type { ChatHandle, ChatProps, ElementSnippet } from "../Chat/chatTypes";
import { FilePreviewPanel } from "../AttachmentHoverPreview";
import type { FilePreviewTarget } from "../FileRender";
import { ImportWorkflowModal } from "../ImportWorkflowModal";
import { ChatWorkspaceLayout } from "./ChatWorkspaceLayout";
import { ChatContent, chatContentSpacing } from "../Chat/ChatContent";
import { Messages } from "../Chat/chatStyles";
import {
  ResearchSourcesPanelContext,
  type ResearchSourceGroup,
} from "../Chat/ResearchCitations";
import { SourcesPanel } from "../Chat/ResearchSourceViews";
import { deriveChatOutputs } from "./chatOutputs";
import { Chatpad, type ChatpadSection } from "./Chatpad";
import { OutputsList } from "./OutputsList";
import { PersonalChatConnectorsBar } from "./PersonalChatConnectorsBar";
import { createWorkflowHandoff } from "./CreateWorkflowHandoff";
import { DeriveWorkflowButton } from "./DeriveWorkflowButton";
import { subAgentTools, useSubAgentStatus } from "./SubAgents";
import { SubAgentPage } from "./SubAgentPage";
import { SubAgentPrompt, usePendingSubAgentCall } from "./SubAgentPrompt";
import { chatSuggestions } from "./chatSlashSuggestions";
import { greetingName, NewChatEmptyState } from "./NewChatEmptyState";
import { getInvestigationContent } from "../../../shared/chatInvestigation";
import { useChatInvestigation } from "./useChatInvestigation";
import { ChatInvestigationSource } from "./ChatInvestigationSource";
import { pickChatIconColor } from "./pickChatIconColor";
import {
  type WorkflowPageInspection,
  type WorkflowPageLink,
  WorkflowPagePreview,
  workflowPageLink,
} from "./WorkflowPagePreview";
import { useRecordChatComposerControl } from "../../hooks/useRecordChatComposerControl";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import { CHAT_WORKFLOWS_FLAG } from "../../../shared/featureFlagNames";
import { mentionConnectorCatalog } from "../../hooks/useMentions";
import { PanelTransition } from "../PanelTransition";
import { ChatpadPreview } from "./ChatpadPreview";
import {
  type ChatWorkflowTarget,
  useChatWorkflowUrl,
} from "../../lib/chatWorkflowUrl";
import { readChatPanelOpen, writeChatPanelOpen } from "../../lib/chatPanelOpen";
import {
  isRouteLocation,
  routes,
  useMakeUrl,
  useRouteMatch,
} from "../../lib/urls";
import { ChatWorkflowBanner } from "../ChatWorkflowBanner";
import { Skeleton } from "../Skeleton";
import {
  WorkflowBackRowSkeleton,
  WorkflowNavigationSkeleton,
} from "../WorkflowNavigation";
import { withoutOpeningFocus } from "../Chat/chatBranchTranscript";
import { useWorkflowSession } from "../Workflow";
import { LinkPreviewContext } from "../Markdown";
import {
  BuildingPagePreview,
  pageBuildFile,
  type PageBuild,
} from "./BuildingPagePreview";

type ChatRow = {
  id: string;
  respondingAt: number | null;
  lastLoopHeartbeatAt: number | null;
  awaitingAction: boolean | null;
  entries: readonly NonNullable<
    Parameters<typeof Chat>[0]["entries"]
  >[number][];
};

type Props = {
  personalChatId: string;
  chatOwnerId: string;
  chat: ChatRow;
  pending?: boolean;
  readOnly?: boolean;
  focus: { spaceId: string; workflowId: string; step?: string } | null;
  workflowNavigationSlot: HTMLElement | null;
  workflowHeaderSlot: HTMLElement | null;
  onWorkflowGone: () => void;
};

const Container = styled("div")`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  container-type: inline-size;
`;

const DeriveWorkflowSlot = styled("div")`
  position: absolute;
  top: 12px;
  right: 52px;
  z-index: 1;
  &[data-panel-open="true"] {
    right: 12px;
  }
`;

const ScrollArea = styled("div")`
  ${chatContentSpacing}
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  container-type: inline-size;
  margin-bottom: var(--chat-content-gutter);
  transition: margin-bottom var(--motion-base) var(--ease-out);
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  &[data-inner-scroll="true"] {
    overflow: hidden;
    margin-bottom: 0;
  }
`;

// A workflow conversation scrolls inside the chat, as on the workflow page, so
// its banner and previous-chat drawer behave the same way.
const ChatWrap = styled(ChatContent)`
  min-height: 100%;
  &[data-inner-scroll="true"] {
    --workflow-chat-gutter: var(--chat-content-gutter);
    flex: 1;
    min-height: 0;
    padding-bottom: var(--chat-content-gutter);
  }
  &[data-inner-scroll="true"] [data-current-conversation="true"] {
    margin-right: 0;
    --chat-table-scroll-inset: 4px;
  }
  /* The 24px floor keeps text clear of the message nav. */
  &[data-inner-scroll="true"]
    [data-current-conversation="true"]:not([data-exploratory="true"]) {
    padding-inline: calc(var(--chat-feed-inset) - var(--chat-content-gutter));
    transition: padding-inline var(--motion-base) var(--ease-out);
  }
`;

const bannerEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(-24px);
  }
`;

const bannerSpaceEnter = keyframes`
  from {
    padding-top: 16px;
  }
`;

const privateEnter = keyframes`
  from {
    background-color: transparent;
    border-color: transparent;
  }
`;

const labelEnter = keyframes`
  from {
    opacity: 0;
  }
`;

const feedEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
`;

// A chat handing off to its draft while open stays in place: the banner slides
// in, its private turns gain their container, and the draft’s turns feed in below.
const HandoffWrap = styled("div")`
  display: contents;
  &[data-enter="chat"] {
    ${Messages} {
      animation: ${bannerSpaceEnter} var(--motion-slow) var(--ease-out);
    }
  }
  &[data-enter] {
    [data-conversation-banner] {
      animation: ${bannerEnter} var(--motion-slow) var(--ease-out) both;
    }
    [data-conversation-transcript] > [data-private="section"] {
      animation: ${privateEnter} var(--motion-slow) var(--ease-out) 120ms both;
    }
    [data-conversation-transcript] > [data-private="label"] {
      animation: ${labelEnter} var(--motion-slow) var(--ease-out) 120ms both;
    }
    [data-conversation-transcript] > :not([data-private]) {
      animation: ${feedEnter} var(--motion-slow) var(--ease-out) both;
    }
  }
`;

const panelEnter = keyframes`
  from {
    opacity: 0;
    transform: translateX(16px);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

const PanelSwap = styled("div")`
  display: flex;
  flex: 1;
  min-width: 0;
  min-height: 0;
  &[data-enter="true"] {
    animation: ${panelEnter} var(--motion-base) var(--ease-out);
  }
`;

const WorkflowPanelBody = styled("div")`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  border-left: 0.5px solid var(--border-alpha);
`;

// ChatWorkspace remounts per tab (keyed by personalChatId), so the open output
// and the dragged panel width are kept here to survive tab switches.
type WorkspacePanel =
  | { type: "outputs" }
  | {
      type: "building-page";
      build: PageBuild;
      parent: WorkspacePanel | null;
    }
  | {
      type: "output";
      output: ChatOutputView;
      parent: WorkspacePanel | null;
    }
  | {
      type: "archive-file";
      file: FilePreviewTarget;
      parent: WorkspacePanel;
    }
  | {
      type: "sources";
      messageId?: string;
    }
  | { type: "page"; link: WorkflowPageLink; parent: WorkspacePanel | null };
const openPanelByChatId = new Map<string, WorkspacePanel>();
const elementSnippetsByChatId = new Map<string, ElementSnippet[]>();
const openedPageCallsByChatId = new Map<string, string>();
const openedPageBuilds = new Set<string>();
const previewTurnsByChatId = new Map<string, number>();

function inChatWorkflowHref(href: string, personalChatId: string) {
  const url = URL.parse(href, location.origin);
  if (
    url?.origin !== location.origin ||
    !isRouteLocation(routes.space.workflow, url.pathname)
  )
    return null;
  url.searchParams.set("chat", personalChatId);
  return `${url.pathname}${url.search}`;
}

function inPlaceModalHref(
  href: string,
  makeUrl: ReturnType<typeof useMakeUrl>
) {
  const url = URL.parse(href, location.origin);
  const modal = url?.searchParams.get("modal");
  const id = url?.searchParams.get("modal[id]");
  if (
    url?.origin !== location.origin ||
    url.pathname !== "/" ||
    !id ||
    (modal !== "connector" && modal !== "skill")
  )
    return null;
  return makeUrl(routes.modal[modal], { id });
}

function deselectPreviewElement(snippet: ElementSnippet) {
  window.dispatchEvent(
    new CustomEvent("3b-deselect-element", {
      detail: { index: snippet.index, sourceId: snippet.sourceId },
    })
  );
}

function panelSection(panel: WorkspacePanel | null): ChatpadSection {
  if (!panel) return "outputs";
  if (panel.type === "page") return panelSection(panel.parent);
  return panel.type === "sources" ? "sources" : "outputs";
}

function panelParent(
  panel: WorkspacePanel | null
): WorkspacePanel | null | undefined {
  switch (panel?.type) {
    case "building-page":
    case "output":
    case "archive-file":
    case "page":
      return panel.parent;
    default:
      return undefined;
  }
}

function panelBackLabel(panel: WorkspacePanel | null | undefined) {
  switch (panel?.type) {
    case "outputs":
      return "Back to files";
    case "sources":
      return "Back to sources";
    case "output":
      return `Back to ${panel.output.fileName}`;
    case "building-page":
      return `Back to ${panel.build.file.fileName}`;
    case "archive-file":
      return `Back to ${panel.file.fileName}`;
    case "page":
      return `Back to ${panel.link.route}`;
    default:
      return "Back to files";
  }
}

function panelDepth(panel: WorkspacePanel | null | undefined): number {
  switch (panel?.type) {
    case "building-page":
    case "output":
    case "archive-file":
    case "page":
      return panelDepth(panel.parent) + 1;
    default:
      return 0;
  }
}

function panelViewKey(panel: WorkspacePanel | null): string {
  switch (panel?.type) {
    case "building-page":
      return `output:${panel.build.file.key}`;
    case "output":
      return `output:${panel.output.key}`;
    case "archive-file":
      return `${panelViewKey(panel.parent)}/${panel.file.fileName}`;
    case "page":
      return `page:${panel.link.href}`;
    default:
      return panel?.type ?? "outputs";
  }
}

// A draft this chat handed off to shows the conversation that led there above its kickoff; only you see it, and the workflow’s assistant never does.
function handoffTranscript(
  chatEntries: ChatProps["entries"],
  draftEntries: ChatProps["entries"],
  draftBranchId: string
) {
  const own = chatEntries.toSorted((a, b) => a.createdAt - b.createdAt);
  // createWorkflow records the agent’s move onto the draft it created.
  const handoffIndex = own.findIndex((entry) => {
    const focus = entry.userAction?.metadata as {
      branchId?: string;
      by?: string;
    } | null;
    return (
      entry.userAction?.actionType === "focusWorkflow" &&
      focus?.by === "agent" &&
      focus.branchId === draftBranchId
    );
  });
  if (handoffIndex < 0) return undefined;
  return [...own.slice(0, handoffIndex), ...draftEntries].toSorted(
    (a, b) => a.createdAt - b.createdAt
  );
}

export function ChatWorkspace({
  personalChatId,
  chatOwnerId,
  chat,
  pending,
  readOnly = false,
  focus,
  workflowNavigationSlot,
  workflowHeaderSlot,
  onWorkflowGone,
}: Props) {
  const z = useZero();
  const chatWorkflows = useFeatureFlag(CHAT_WORKFLOWS_FLAG);
  const [bannerSlot, setBannerSlot] = useState<HTMLDivElement | null>(null);
  const [messageNavContainer, setMessageNavContainer] =
    useState<HTMLDivElement | null>(null);
  const recordControl = useRecordChatComposerControl();
  const edgeUrlOptions = useSessionTenantEdgeUrlOptions();
  const [currentUser] = useQuery(queries.currentUser());
  const userName = greetingName(currentUser?.name ?? "");
  const [personalChat] = useQuery(
    queries.loadPersonalChat({ personalChatId, userId: chatOwnerId })
  );
  const mentionConnectors = useMemo(() => {
    const seen = new Set<string>();
    return (personalChat?.appUsages ?? []).flatMap((row) => {
      const app = row.connectedApp;
      if (!app || seen.has(app.name)) return [];
      seen.add(app.name);
      return [{ name: app.name, identifier: app.identifier }];
    });
  }, [personalChat?.appUsages]);
  const [tenantConnectorsRaw] = useQuery(queries.connectorsIndex());
  const mentionTenantConnectors = useMemo(() => {
    const seen = new Set<string>();
    return tenantConnectorsRaw.flatMap((app) => {
      if (seen.has(app.name)) return [];
      seen.add(app.name);
      return [{ name: app.name, identifier: app.identifier }];
    });
  }, [tenantConnectorsRaw]);
  const [pendingZip, setPendingZip] = useState<File | null>(null);
  const [elementSnippets, setElementSnippets] = useState<ElementSnippet[]>(
    () => elementSnippetsByChatId.get(personalChatId) ?? []
  );
  useEffect(() => {
    if (elementSnippets.length)
      elementSnippetsByChatId.set(personalChatId, elementSnippets);
    else elementSnippetsByChatId.delete(personalChatId);
  }, [personalChatId, elementSnippets]);
  const selectElement = (element: ElementSnippet) => {
    setElementSnippets((previous) => [
      ...previous.filter(
        (snippet) =>
          snippet.sourceId !== element.sourceId ||
          snippet.index !== element.index
      ),
      element,
    ]);
  };
  const deselectElement = (sourceId: string, index: number) => {
    setElementSnippets((previous) =>
      previous.filter(
        (snippet) => snippet.sourceId !== sourceId || snippet.index !== index
      )
    );
  };
  const removeElementSnippet = (id: string) => {
    const snippet = elementSnippets.find((element) => element.id === id);
    if (snippet) deselectPreviewElement(snippet);
    setElementSnippets((previous) =>
      previous.filter((element) => element.id !== id)
    );
  };
  const clearElementSnippets = () => {
    elementSnippets.forEach(deselectPreviewElement);
    setElementSnippets([]);
  };
  const [sourceGroups, setSourceGroups] = useState<ResearchSourceGroup[]>([]);
  const [panel, setPanel] = useState<WorkspacePanel | null>(
    () => openPanelByChatId.get(personalChatId) ?? null
  );
  const [panelOpen, setPanelOpen] = useState(readChatPanelOpen);
  useEffect(() => writeChatPanelOpen(panelOpen), [panelOpen]);
  const chatWorkflowUrl = useChatWorkflowUrl();
  const makeUrl = useMakeUrl();
  const [, { navigation }] = useRouteMatch(
    focus?.spaceId ? routes.space.workflow : routes.personal.workflow
  );
  const scrollRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const chatRef = useRef<ChatHandle>(null);
  const showOutput = useCallback((next: ChatOutputView) => {
    setPanel((previous) => {
      while (previous?.type === "archive-file") previous = previous.parent;
      return {
        type: "output",
        output: next,
        parent:
          previous?.type === "output" || previous?.type === "building-page"
            ? previous.parent
            : previous,
      };
    });
    setPanelOpen(true);
  }, []);
  const showArchiveFile = useCallback(
    (file: FilePreviewTarget) => {
      if (panel?.type !== "output" && panel?.type !== "archive-file") return;
      // Extraction may finish after the user navigates away from the archive.
      setPanel((previous) =>
        previous === panel
          ? { type: "archive-file", file, parent: panel }
          : previous
      );
    },
    [panel]
  );
  const showSources = useCallback((group: ResearchSourceGroup) => {
    setPanel({
      type: "sources",
      messageId: group.messageId,
    });
    setPanelOpen(true);
  }, []);
  const showAllSources = useCallback(() => {
    setPanel({ type: "sources" });
    setPanelOpen(true);
  }, []);
  const openPage = useCallback((link: WorkflowPageLink) => {
    setPanel((previous) => ({
      type: "page",
      link,
      parent: previous?.type === "page" ? previous.parent : previous,
    }));
    setPanelOpen(true);
  }, []);
  const previewLink = (href: string) => {
    const link = workflowPageLink(href, edgeUrlOptions);
    if (!link) return false;
    openPage(link);
    return true;
  };
  const showPage = (view: WorkflowPageView) => {
    if (!chatWorkflows || !view.spaceId || !view.stepDir) {
      const link = workflowPageLink(
        getPageUrl(view.spaceSlug, view.route, edgeUrlOptions, {
          branchId: view.branchId,
          externalId: view.externalId,
        }),
        edgeUrlOptions
      );
      if (link) openPage({ ...link, workflowId: view.workflowId });
      return;
    }
    history.pushState(
      history.state,
      "",
      makeUrl(
        routes.space.workflow,
        {
          spaceId: view.spaceId,
          workflowId: view.workflowId,
          branchId: view.branchId ?? "",
          tab: "browser",
          browserStep: view.stepDir,
          chatId: personalChatId,
          navigation: view.workflowId === focus?.workflowId ? navigation : "",
        },
        { inheritParams: false }
      )
    );
    setPanelOpen(true);
  };
  const viewedPageWorkflowId =
    panel?.type === "page" ? panel.link.workflowId : undefined;
  useEffect(() => {
    if (viewedPageWorkflowId)
      z.mutate(
        sharedMutators.markWorkflowViewed({ workflowId: viewedPageWorkflowId })
      );
  }, [viewedPageWorkflowId, z]);
  const [previewTurnStartedAt, setPreviewTurnStartedAt] = useState(
    () => chat.respondingAt ?? previewTurnsByChatId.get(personalChatId) ?? null
  );
  if (chat.respondingAt != null && chat.respondingAt !== previewTurnStartedAt)
    setPreviewTurnStartedAt(chat.respondingAt);
  useEffect(() => {
    if (previewTurnStartedAt != null)
      previewTurnsByChatId.set(personalChatId, previewTurnStartedAt);
  }, [personalChatId, previewTurnStartedAt]);
  const buildingEntry =
    previewTurnStartedAt == null
      ? undefined
      : chat.entries.findLast(
          (entry) =>
            entry.createdAt >= previewTurnStartedAt && pageBuildFile(entry)
        );
  const buildingFile = buildingEntry && pageBuildFile(buildingEntry);
  const openBuildingPage = useEffectEvent((build: PageBuild) => {
    setPanel((previous) => ({
      type: "building-page",
      build,
      parent:
        previous?.type === "building-page" || previous?.type === "output"
          ? previous.parent
          : previous,
    }));
    setPanelOpen(true);
  });
  useEffect(() => {
    if (
      readOnly ||
      chat.respondingAt == null ||
      !buildingEntry ||
      !buildingFile ||
      buildingEntry.toolCall?.completedAt != null ||
      buildingEntry.toolCall?.isError ||
      openedPageBuilds.has(buildingEntry.id)
    )
      return;
    openedPageBuilds.add(buildingEntry.id);
    // oxlint-disable-next-line react/set-state-in-effect -- Navigation follows a streamed tool call declaring a new page build.
    openBuildingPage({ entryId: buildingEntry.id, file: buildingFile });
  }, [buildingEntry, buildingFile, chat.respondingAt, readOnly]);
  const autoShowOutput = (next: ChatOutputView) => {
    if (
      buildingEntry &&
      buildingFile?.key === next.key &&
      openedPageBuilds.has(buildingEntry.id)
    ) {
      setPanel((previous) =>
        (previous?.type === "building-page" &&
          previous.build.entryId === buildingEntry.id) ||
        (previous?.type === "output" && previous.output.key === next.key)
          ? { type: "output", output: next, parent: previous.parent }
          : previous
      );
    } else showOutput(next);
  };
  const pageRequest =
    previewTurnStartedAt != null
      ? chat.entries.findLast(
          (entry) =>
            entry.createdAt >= previewTurnStartedAt &&
            entry.toolCall?.toolName === "showWorkflowPage" &&
            !entry.toolCall.isError
        )
      : undefined;
  const { output: requestedPage } = useToolOutput(
    pageRequest?.id ?? "",
    pageRequest?.toolCall?.completedAt != null
  );
  const pageRequestId = pageRequest?.id;
  const showRequestedPage = useEffectEvent(showPage);
  useEffect(() => {
    if (
      !pageRequestId ||
      openedPageCallsByChatId.get(personalChatId) === pageRequestId
    )
      return;
    const view = workflowPageViewSchema.safeParse(requestedPage);
    if (!view.success) return;
    openedPageCallsByChatId.set(personalChatId, pageRequestId);
    // oxlint-disable-next-line react/set-state-in-effect -- Navigation follows asynchronously loaded tool results.
    showRequestedPage(view.data);
  }, [pageRequestId, personalChatId, requestedPage]);
  const investigationDraft = useChatInvestigation(
    personalChatId,
    readOnly,
    chat.entries.length > 0
  );
  const investigation = investigationDraft.context;
  const investigationDetails = investigation
    ? getInvestigationContent(investigation.surface)
    : undefined;
  // Keep the component identity stable so composer updates preserve the selected category.
  const renderEmptyState = useCallback(
    ({
      fillInput,
      visible,
    }: ComponentProps<NonNullable<ChatProps["EmptyState"]>>) => (
      <NewChatEmptyState
        onPick={fillInput}
        iconColor={`var(--${pickChatIconColor(personalChatId)}-600)`}
        userName={userName}
        seed={personalChatId}
        visible={visible}
        content={investigationDetails}
      />
    ),
    [personalChatId, userName, investigationDetails]
  );
  const closeOutputs = useCallback(() => setPanelOpen(false), []);
  const showOutputs = useCallback(() => {
    setPanel({ type: "outputs" });
    setPanelOpen(true);
  }, []);
  // A chat that has become one workflow’s conversation stays that way; picking
  // another workflow opens that workflow’s own conversation instead.
  const openWorkflow = (workflow: ChatWorkflowTarget) => {
    setPanelOpen(true);
    if (focus?.workflowId === workflow.id) return;
    history.pushState(
      history.state,
      "",
      focus
        ? makeUrl(
            routes.space.workflow,
            {
              spaceId: workflow.spaceId,
              workflowId: workflow.id,
              ...(workflow.branchId && { branchId: workflow.branchId }),
            },
            { inheritParams: false }
          )
        : chatWorkflowUrl(personalChatId, workflow)
    );
  };
  const showSection = useCallback(
    (section: ChatpadSection) => {
      if (section === "outputs") showOutputs();
      else showAllSources();
    },
    [showOutputs, showAllSources]
  );
  const toggleChatpad = useCallback(() => {
    if (!focus)
      recordControl("chatpad", panelOpen ? "close" : "open", personalChatId);
    setPanelOpen(!panelOpen);
  }, [focus, panelOpen, recordControl, personalChatId]);
  const goBack = useCallback(() => {
    setPanel((previous) => panelParent(previous) ?? null);
    setPanelOpen(true);
  }, []);
  const sourcesContext = useMemo(
    () => ({ showSources, onSourcesChange: setSourceGroups }),
    [showSources]
  );
  const outputs = useMemo(
    () => deriveChatOutputs(chat.entries as ChatEntryWithSubTables[]),
    [chat.entries]
  );
  useEffect(() => {
    if (panel) openPanelByChatId.set(personalChatId, panel);
    else openPanelByChatId.delete(personalChatId);
  }, [personalChatId, panel]);
  useChatpadShortcut(toggleChatpad);
  const pendingSubAgentCall = usePendingSubAgentCall(chat.id);
  const subAgentStatus = useSubAgentStatus(chat.id, chat.entries);
  const tools: readonly ChatTool[] = [
    askQuestion,
    approvePlan,
    bash({ getStepColor: () => undefined }),
    connectChatToApp({ personalChatId, chatOwnerId }),
    connectToApp({ personalChatId, chatOwnerId }),
    editFile({ getStepColor: () => undefined }),
    execute,
    memory,
    ...subAgentTools(chat.id),
    createWorkflowHandoff,
    setWorkflowSuggestion,
    showExecutionOutput({
      respondingAt: readOnly ? null : previewTurnStartedAt,
      onShowOutput: showOutput,
      onAutoShowOutput: autoShowOutput,
    }),
    showStorageFile({
      respondingAt: readOnly ? null : previewTurnStartedAt,
      onShowOutput: showOutput,
      onAutoShowOutput: autoShowOutput,
    }),
    showWorkflowPage(showPage),
    readFile({ getStepColor: () => undefined }),
    readStorageFile,
    skillTool,
    readSkillFile,
    runSkillScript,
    webFetch,
    webSearch,
    writeFile({ getStepColor: () => undefined }),
  ];

  useEffect(() => {
    if (pending) return;
    z.mutate(sharedMutators.touchPersonalChat({ id: personalChatId }));
  }, [personalChatId, pending, z]);

  const handleSend: ChatProps["onSend"] = (payload, onError) => {
    const result = z.mutate(
      sharedMutators.sendPersonalChatMessage({
        chatId: chat.id,
        entryId: nanoid(12),
        text: payload.text,
        isDictated: payload.isDictated,
        effort: payload.effort,
        chatModel: payload.chatModel,
        attachments: payload.attachments,
        interruptEntryId: payload.interruptEntryId,
        ...clientSendContext(),
      })
    );
    void result.server.then((r) => {
      if (r.type === "error") onError();
    }, onError);
  };

  const handleInterrupt = () => {
    z.mutate(
      sharedMutators.interruptPersonalChat({
        entryId: nanoid(12),
        chatId: chat.id,
      })
    );
  };

  const handleProvideToolResult = useCallback<ChatProps["onProvideToolResult"]>(
    async (entryId, output, isError, options) => {
      const showSaveError = () => {
        emitErrorToast({
          icon: "circle-off",
          message: "The tool result couldn’t be saved.",
          buttonLabel: "Dismiss",
        });
        return false;
      };
      try {
        const outputId = await uploadToolCallOutput({
          chatId: chat.id,
          entryId,
          output,
        });
        const result = await z.mutate(
          sharedMutators.providePersonalChatToolResult({
            chatId: chat.id,
            entryId,
            outputId,
            isError,
            pauseAfter: options?.pauseAfter,
            chatModel: options?.chatModel,
          })
        ).server;
        if (result.type === "error") return showSaveError();
        return true;
      } catch {
        return showSaveError();
      }
    },
    [chat.id, z]
  );

  const session = useWorkflowSession({
    focus: readOnly ? null : focus,
    chatRef,
    standalone: false,
    onClosePanel: () => setPanelOpen(false),
    onExit: onWorkflowGone,
    navigationHeaderSlot: workflowHeaderSlot,
  });
  const subAgentPrompt = pendingSubAgentCall &&
    !readOnly &&
    !chat.awaitingAction && <SubAgentPrompt {...pendingSubAgentCall} />;
  const sessionLoading = !!focus && !readOnly && !session;
  const chatEntries = withoutOpeningFocus(chat.entries);
  const personalChatProps: ChatProps = {
    modelScope: { type: "personal" },
    onZipFile: chat.entries.length > 0 ? undefined : setPendingZip,
    acceptAnyFile: true,
    entries: chatEntries,
    writeChatId: chat.id,
    draftKey: personalChatId,
    draftPathname: "/chats",
    onSend: handleSend,
    onInterrupt: handleInterrupt,
    onProvideToolResult: handleProvideToolResult,
    isCurrentUserChat: !readOnly,
    canWrite: !readOnly,
    tools,
    elementSnippets,
    removeElementSnippet,
    clearElementSnippets,
    mentionConnectors,
    mentionTenantConnectors,
    mentionConnectorCatalog,
    mentionConnectorContextLabel: "On this chat",
    composerContext: {
      ...investigationDraft,
      pill:
        investigation && investigationDetails ? (
          <ChatInvestigationSource
            context={investigation}
            onRemove={investigationDraft.clear}
          />
        ) : undefined,
    },
    suggestions: chatSuggestions,
    placeholder: "Ask anything across your tools and data",
    EmptyState: renderEmptyState,
    soundsEnabled: !readOnly,
    respondingAt: chat.respondingAt,
    lastLoopHeartbeatAt: chat.lastLoopHeartbeatAt,
    awaitingAction: chat.awaitingAction ?? false,
    subAgentStatus,
    pendingPromptOverride: subAgentPrompt,
    planModeEnabled: false,
    resolveFileHref: readOnly
      ? undefined
      : (href) =>
          (chatWorkflows ? inChatWorkflowHref(href, personalChatId) : null) ??
          inPlaceModalHref(href, makeUrl) ??
          resolveStorageFileHref(href, personalVolumePath),
    outerScroll: true,
    outerScrollRef: scrollRef,
  };
  const focusWorkflowId = session?.workflow.id;
  const focusWorkflowName = session?.workflow.name;
  const focusBranchId =
    session && !session.isOnMainBranch ? session.branch.id : null;
  const focusBranchName =
    session && !session.isOnMainBranch ? session.branch.name : null;
  const recordedWorkflowId = personalChat?.workflowId ?? null;
  const recordedBranchId = personalChat?.branchId ?? null;
  const focusRecorded =
    !personalChat ||
    !focusWorkflowId ||
    (recordedWorkflowId === focusWorkflowId &&
      recordedBranchId === focusBranchId);
  // Forking from live and pushing live both move a chat between versions of its workflow.
  const focusMovable =
    !recordedWorkflowId || recordedWorkflowId === focusWorkflowId;
  // When the agent moves the chat onto other work, the page follows it there.
  const recordedKey = personalChat
    ? `${recordedWorkflowId}:${recordedBranchId}`
    : undefined;
  const urlKey = focus
    ? session
      ? `${focusWorkflowId}:${focusBranchId}`
      : undefined
    : "null:null";
  const [lastRecordedKey, setLastRecordedKey] = useState(recordedKey);
  const [followKey, setFollowKey] = useState<string | null>(null);
  if (recordedKey !== lastRecordedKey) {
    setLastRecordedKey(recordedKey);
    if (lastRecordedKey !== undefined && recordedWorkflowId && recordedKey)
      setFollowKey(recordedKey);
  }
  if (followKey && followKey === urlKey) setFollowKey(null);
  const [followedWorkflow] = useQuery(
    queries.loadWorkflow({ workflowId: recordedWorkflowId ?? "" }),
    { enabled: !!followKey }
  );
  const followUrl =
    followKey && followedWorkflow && recordedWorkflowId
      ? makeUrl(
          routes.space.workflow,
          {
            spaceId: followedWorkflow.spaceId,
            workflowId: recordedWorkflowId,
            branchId: recordedBranchId ?? "",
            chatId: personalChatId,
            navigation:
              recordedWorkflowId === focus?.workflowId ? navigation : "",
          },
          { inheritParams: false }
        )
      : null;
  useEffect(() => {
    if (followUrl) history.pushState(history.state, "", followUrl);
  }, [followUrl]);
  // A rejected focus rolls back, so remember what was asked to avoid retrying it every render.
  const requestedFocusRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      readOnly ||
      followKey ||
      focusRecorded ||
      !focusWorkflowId ||
      !focusWorkflowName
    )
      return;
    // A chat works on one workflow, so any other opens in its own chat.
    if (!focusMovable) {
      const url = new URL(location.href);
      url.searchParams.delete("chat");
      history.replaceState(history.state, "", `${url.pathname}${url.search}`);
      return;
    }
    const key = `${personalChatId}:${focusWorkflowId}:${focusBranchId}`;
    if (requestedFocusRef.current === key) return;
    requestedFocusRef.current = key;
    z.mutate(
      sharedMutators.setChatFocus({
        id: personalChatId,
        entryId: nanoid(12),
        workflowId: focusWorkflowId,
        branchId: focusBranchId,
        workflowName: focusWorkflowName,
        branchName: focusBranchName,
      })
    );
  }, [
    readOnly,
    followKey,
    focusRecorded,
    focusMovable,
    personalChatId,
    focusWorkflowId,
    focusWorkflowName,
    focusBranchId,
    focusBranchName,
    z,
  ]);
  const draftBranchId =
    session && !session.isOnMainBranch ? session.branch.id : null;
  const handoffEntries =
    draftBranchId && session
      ? handoffTranscript(
          chat.entries,
          session.chatProps.entries,
          draftBranchId
        )
      : undefined;
  // Only a handoff seen happening animates, from this chat or the workflow’s live version; opening a draft handed off earlier doesn’t.
  const view = !focus
    ? "chat"
    : !session
      ? null
      : session.isOnMainBranch
        ? `live:${session.workflow.id}`
        : `draft:${session.branch.id}`;
  const [lastView, setLastView] = useState(view);
  const [entered, setEntered] = useState<{
    draft: string;
    from: "chat" | "live";
  } | null>(null);
  if (view && view !== lastView) {
    setLastView(view);
    setEntered(
      view.startsWith("draft:") && lastView === "chat"
        ? { draft: view, from: "chat" }
        : view.startsWith("draft:") &&
            lastView === `live:${session?.workflow.id}`
          ? { draft: view, from: "live" }
          : null
    );
  }
  const handoffEnter =
    entered?.draft === view &&
    (!!handoffEntries || !!session?.chatProps.privateChatIds?.length)
      ? entered.from
      : undefined;
  const bannerSlotNode = (
    <div ref={setBannerSlot} style={{ display: "contents" }} />
  );
  const chatProps: ChatProps = session
    ? {
        ...session.chatProps,
        entries: handoffEntries ?? session.chatProps.entries,
        privateChatIds: handoffEntries
          ? [chat.id, ...(session.chatProps.privateChatIds ?? [])]
          : session.chatProps.privateChatIds,
        conversationBanner: bannerSlotNode,
        // A draft’s earlier history reads like live’s, above its banner, so moving between them leaves it in place.
        ...(session.chatProps.previousConversation && {
          exploratoryConversation: {
            live: session.chatProps.previousConversation,
            bannerNode: bannerSlotNode,
          },
          previousConversation: undefined,
        }),
        messageNavPortal: {
          container: messageNavContainer,
          wrapperRef: containerRef,
        },
      }
    : personalChatProps;

  // Only a change of mode while this chat is open animates the panel, so
  // switching between chats (which remounts this) shows it in place.
  const panelMode = focus ? "workflow" : "chatpad";
  const [settledPanelMode, setSettledPanelMode] = useState(panelMode);
  const [enteredPanelMode, setEnteredPanelMode] = useState<string | null>(null);
  if (panelMode !== settledPanelMode && (!focus || session)) {
    setSettledPanelMode(panelMode);
    setEnteredPanelMode(panelMode);
  }

  const chatPane = (
    <Container ref={containerRef}>
      {chatWorkflows === false && !readOnly && (
        <DeriveWorkflowSlot data-panel-open={panelOpen}>
          <DeriveWorkflowButton
            personalChatId={personalChatId}
            chatOwnerId={chatOwnerId}
          />
        </DeriveWorkflowSlot>
      )}
      <ScrollArea ref={scrollRef} data-inner-scroll={!!session}>
        <ChatWrap data-inner-scroll={!!session}>
          {/* Otherwise the chat’s own empty state flashes before the workflow’s conversation replaces it. */}
          {!sessionLoading && (
            <HandoffWrap data-enter={handoffEnter}>
              <Chat
                key={handoffEntries || !session ? chat.id : session.chatKey}
                ref={chatRef}
                {...chatProps}
              />
            </HandoffWrap>
          )}
          {!readOnly && (
            <SubAgentPage
              chatId={chat.id}
              entries={chat.entries}
              onShowWorkflow={openWorkflow}
              onShowPage={showPage}
            />
          )}
          {session
            ? session.zipModal
            : pendingZip && (
                <ImportWorkflowModal
                  file={pendingZip}
                  onClose={() => setPendingZip(null)}
                />
              )}
        </ChatWrap>
      </ScrollArea>
      <ChatMessageNavOverlay ref={setMessageNavContainer}>
        {!session && (
          <ChatMessageNav
            entries={chatEntries as ChatEntryWithSubTables[]}
            scrollContainer={scrollRef}
            wrapperRef={containerRef}
          />
        )}
      </ChatMessageNavOverlay>
    </Container>
  );

  const backLabel = panelBackLabel(panelParent(panel));
  const pageInspection: WorkflowPageInspection | undefined = readOnly
    ? undefined
    : {
        elementSnippets,
        onElementSelect: (element) => selectElement(element),
        onElementDeselect: deselectElement,
      };

  return (
    <ResearchSourcesPanelContext.Provider value={sourcesContext}>
      <LinkPreviewContext.Provider value={previewLink}>
        <ChatWorkspaceLayout
          open={panelOpen}
          onOpen={toggleChatpad}
          onClose={closeOutputs}
          panel={
            focus ? (
              <PanelSwap
                key="workflow"
                data-enter={enteredPanelMode === "workflow"}
              >
                {sessionLoading ? (
                  <>
                    {workflowHeaderSlot &&
                      createPortal(
                        <WorkflowBackRowSkeleton />,
                        workflowHeaderSlot
                      )}
                    {workflowNavigationSlot &&
                      createPortal(
                        <WorkflowNavigationSkeleton />,
                        workflowNavigationSlot
                      )}
                    <WorkflowPanelBody>
                      <Skeleton
                        style={{ flex: 1, margin: 12, borderRadius: 12 }}
                      />
                    </WorkflowPanelBody>
                  </>
                ) : (
                  session?.frame((navigation, _banner, parts) => (
                    <>
                      {bannerSlot &&
                        createPortal(
                          <ChatWorkflowBanner
                            parts={parts}
                            entering={enteredPanelMode === "workflow"}
                          />,
                          bannerSlot
                        )}
                      {workflowNavigationSlot &&
                        createPortal(navigation, workflowNavigationSlot)}
                      <WorkflowPanelBody ref={session.observeContainer}>
                        {session.panel}
                      </WorkflowPanelBody>
                    </>
                  ))
                )}
              </PanelSwap>
            ) : (
              <PanelSwap
                key="chatpad"
                data-enter={enteredPanelMode === "chatpad"}
              >
                <Chatpad
                  open={panelOpen}
                  onToggle={toggleChatpad}
                  activeSection={panelSection(panel)}
                  onSelectSection={showSection}
                  connectors={
                    <PersonalChatConnectorsBar
                      personalChatId={personalChatId}
                      chatOwnerId={chatOwnerId}
                      readOnly={readOnly}
                    />
                  }
                >
                  <PanelTransition
                    viewKey={panelViewKey(panel)}
                    depth={panelDepth(panel)}
                  >
                    {panel?.type === "sources" ? (
                      <SourcesPanel
                        embedded
                        groups={sourceGroups}
                        scrollTarget={panel}
                        onClose={closeOutputs}
                      />
                    ) : panel?.type === "building-page" ? (
                      <BuildingPagePreview
                        build={panel.build}
                        entries={chat.entries}
                        responding={chat.respondingAt != null}
                        awaitingAction={chat.awaitingAction ?? false}
                        onBack={goBack}
                        backLabel={backLabel}
                      />
                    ) : panel?.type === "output" ? (
                      <FilePreviewPanel
                        // A rewritten file keeps its key and URL, so each show step remounts to refetch it.
                        key={`${panelViewKey(panel)}:${panel.output.toolUseId ?? ""}`}
                        embedded
                        url={panel.output.url}
                        fileName={panel.output.fileName}
                        sizeBytes={panel.output.sizeBytes}
                        onBack={goBack}
                        backLabel={backLabel}
                        interactive
                        htmlInspection={
                          readOnly
                            ? undefined
                            : {
                                sourceId: panel.output.key,
                                elementSnippets: elementSnippets.filter(
                                  (snippet) =>
                                    snippet.sourceId === panel.output.key
                                ),
                                onElementSelect: (element) =>
                                  selectElement({
                                    ...element,
                                    sourceId: panel.output.key,
                                    stepName: panel.output.fileName,
                                    color: "sky",
                                  }),
                                onElementDeselect: (index) =>
                                  deselectElement(panel.output.key, index),
                              }
                        }
                        onOpenFile={showArchiveFile}
                      />
                    ) : panel?.type === "archive-file" ? (
                      <FilePreviewPanel
                        key={panelViewKey(panel)}
                        embedded
                        {...panel.file}
                        onBack={goBack}
                        backLabel={backLabel}
                        interactive
                        onOpenFile={showArchiveFile}
                      />
                    ) : panel?.type === "page" ? (
                      <ChatpadPreview
                        title={panel.link.route}
                        backLabel={backLabel}
                        onBack={goBack}
                      >
                        <WorkflowPagePreview
                          link={panel.link}
                          inspection={pageInspection}
                        />
                      </ChatpadPreview>
                    ) : (
                      <OutputsList
                        outputs={outputs}
                        onShowOutput={showOutput}
                      />
                    )}
                  </PanelTransition>
                </Chatpad>
              </PanelSwap>
            )
          }
        >
          {chatPane}
        </ChatWorkspaceLayout>
      </LinkPreviewContext.Provider>
    </ResearchSourcesPanelContext.Provider>
  );
}
